import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  notFound,
  Outlet,
  redirect,
  useParams,
  useRouter,
} from "@tanstack/react-router"
import { ArrowLeft, EditIcon } from "lucide-react"
import { useMemo, useState } from "react"
import * as z from "zod"

import type { ExtendedClient } from "@repo/supabase/database-types"
import { Button } from "@repo/ui/components/ui/button"
import { If } from "@repo/ui/components/ui/if"
import { PagePanel } from "@repo/ui/components/ui/page-panel"
import { Tabs, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs"
import { useIsMobile } from "@repo/ui/hooks/use-mobile"
import { cn } from "@repo/ui/lib/utils"
import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { getExtendedBusinessEntitiesQueryOptions } from "~/modules/business-entities/business-entities.queries"
import { getExtendedClientsQueryOptions } from "~/modules/clients/clients.queries"
import { ClientsSearchList } from "~/modules/clients/components/clients-search-list"
import { EditProjectModal } from "~/modules/projects/components/edit-project-modal"
import { getExtendedProjectQueryOptions } from "~/modules/projects/projects.queries"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

const log = logger.child({
  module: "projects-project-route",
})

const searchSchema = z.object({
  be_slug: z.string().optional(),
  client_slug: z.string().optional(),
})

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/projects/$projectId",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => pick(search, ["be_slug", "client_slug"]),
  beforeLoad: async ({ location, params, search }) => {
    // Redirect to the overview route if this naked route is accessed directly
    if (location.pathname === `/projects/${params.projectId}`) {
      throw redirect({
        to: "./overview",
        from: "/projects/$projectId",
        params,
        search,
      })
    }
  },
  loader: async ({ context, params }) => {
    context.queryClient
      .ensureQueryData(getExtendedBusinessEntitiesQueryOptions())
      .catch((error) => {
        log.error("Error getting extended business entities", { error })
        return null
      })
    context.queryClient
      .ensureQueryData(
        getExtendedProjectQueryOptions({ projectId: params.projectId }),
      )
      .catch((error) => {
        log.error("Error getting extended project", { error })
        return null
      })
  },
})

function RouteComponent() {
  const { isProjectManagementOn, isProjectEditOn } = useFeatureConfigs()

  if (!isProjectManagementOn) {
    throw notFound()
  }

  const isMobile = useIsMobile()
  const { be_slug, client_slug } = Route.useSearch()
  const params = useParams({ from: Route.id })
  const router = useRouter()
  const [editProjectModalOpen, setEditProjectModalOpen] = useState(false)

  const { data: businessEntitiesData } = useSuspenseQuery(
    getExtendedBusinessEntitiesQueryOptions(),
  )

  const { data: project } = useSuspenseQuery(
    getExtendedProjectQueryOptions({ projectId: params.projectId }),
  )

  const selectedBusinessEntity = useMemo(
    () =>
      businessEntitiesData.businessEntities.find(
        (entity) => entity.slug === be_slug,
      ),
    [businessEntitiesData, be_slug],
  )

  const businessEntityFilterOptions = businessEntitiesData.businessEntities.map(
    (entity) => ({ label: entity.name, value: entity.slug }),
  )

  const { data: clientsData, isLoading: isClientsLoading } = useQuery(
    getExtendedClientsQueryOptions({
      filters: {
        page: 1,
        page_size: 1000, // TODO: This should be replaced with an infinite scroll list (SearchList needs to be updated to support this)
        columnFilters: selectedBusinessEntity?.unit_id
          ? [
              {
                id: "business_entity_unit_ids",
                value: [selectedBusinessEntity.unit_id],
              },
            ]
          : undefined,
      },
    }),
  )

  const selectedClient = useMemo(
    () => clientsData?.clients.find((client) => client.slug === client_slug),
    [clientsData, client_slug],
  )

  const onBack = () => {
    router.navigate({
      to: "/projects",
      search: (prev) => prev,
    })
  }

  const onSelectBusinessEntity = (businessEntitySlug: string | null) => {
    router.navigate({
      to: "/projects",
      search: (prev) => ({
        ...prev,
        client_slug: undefined,
        be_slug: businessEntitySlug ?? undefined,
      }),
    })
  }

  const onSelectClient = (client: ExtendedClient | null) => {
    router.navigate({
      to: "/projects",
      search: (prev) => ({
        ...prev,
        client_slug: client?.slug ?? undefined,
      }),
    })
  }

  const handleEditProject = () => {
    setEditProjectModalOpen(true)
  }

  const currentPath = router.state.location.pathname
  const activeTab = currentPath.split("/").pop()
  const onTabChange = (tab: string) => {
    router.navigate({ to: `../${tab}`, search: (prev) => prev })
  }

  return (
    <>
      <PagePanel title={project?.name || "Project"}>
        <div className="flex h-full flex-col gap-4 md:flex-row">
          <div
            className={cn("block md:block md:w-1/3 lg:w-1/4", {
              hidden: isMobile,
            })}
          >
            <ClientsSearchList
              clients={clientsData?.clients ?? []}
              selectedClientSlug={selectedClient?.slug ?? null}
              onSelectClient={onSelectClient}
              filterOptions={businessEntityFilterOptions}
              filterValue={selectedBusinessEntity?.slug ?? null}
              setFilterValue={onSelectBusinessEntity}
              filterPlaceholder="Select a business entity"
              isLoading={isClientsLoading}
            />
          </div>
          <div className={cn("block flex-1 md:w-2/3 lg:w-3/4")}>
            <div className="glass-darker flex h-full flex-col rounded-lg">
              <div className="flex-1 overflow-auto">
                <Tabs
                  defaultValue={activeTab}
                  onValueChange={onTabChange}
                  className="flex h-full flex-col rounded-lg"
                >
                  <div className="flex items-center gap-4 border-white/10 border-b px-3 py-2.5">
                    <Button variant="ghost" size="sm" onClick={onBack}>
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div>
                      <div className="text-xl">
                        {project?.name || "Project"}
                      </div>
                      <p className="text-sm text-white/70">
                        {project?.client_name}
                      </p>
                    </div>
                    <If condition={isProjectEditOn}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleEditProject}
                        className="group"
                      >
                        <EditIcon className="mr-2 h-4 w-4" />
                        <span className="opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                          Edit Project
                        </span>
                      </Button>
                    </If>
                    <div className="ml-auto">
                      <TabsList>
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="documents">Documents</TabsTrigger>
                        <TabsTrigger value="client-rates">
                          Client Rates
                        </TabsTrigger>
                      </TabsList>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto p-6">
                    <Outlet />
                  </div>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </PagePanel>

      <If condition={isProjectEditOn}>
        <EditProjectModal
          open={editProjectModalOpen}
          onOpenChange={setEditProjectModalOpen}
          project={project}
        />
      </If>
    </>
  )
}
